import { Agent } from "@caupulican/pi-agent-core/agent";
import type { AgentTool, StreamFn } from "@caupulican/pi-agent-core/types";
import type { Api, Model, SimpleStreamOptions } from "@caupulican/pi-ai";
import { SCOUT_SYSTEM_PROMPT } from "./provider-prompt-contracts.ts";
import { registerInFlightWork } from "./reload-blockers.ts";

export { SCOUT_SYSTEM_PROMPT };

export interface ScoutCitation {
	path: string;
	start: number;
	end: number;
	valid: boolean;
}

export interface ScoutRunResult {
	summary: string;
	citations: ScoutCitation[];
	droppedCitations: number;
	unreliable: boolean;
	truncated: boolean;
	turnsUsed: number;
	failure?: string;
}

export interface ScoutControllerDeps {
	resolveScoutModel(): Promise<
		| {
				model: Model<Api>;
				apiKey?: string;
				headers?: Record<string, string>;
				textToolCallProtocol?: SimpleStreamOptions["textToolCallProtocol"];
		  }
		| { failure: string }
	>;
	getCwd(): string;
	buildReadOnlyTools(cwd: string): AgentTool<any>[];
	streamFn?: StreamFn;
	fileExists(path: string): boolean;
	countLines(path: string): number | undefined;
	onEvent?(event: { type: "scout_turn" | "scout_end"; detail: string }): void;
	signal?: AbortSignal;
	/** Registers this scout's own Agent run in the reload-gate quiesce registry while it drives
	 * turns, so `/reload`/profile-switch/live extension load-unload-reconcile wait it out. Optional —
	 * a caller that hasn't wired an agent dir through (e.g. an existing test construction) sees no
	 * behavior change; the scout simply stays invisible to the reload gate, matching its behavior
	 * before this registration was added. */
	getAgentDir?(): string;
}

const MAX_TURNS_DEFAULT = 8;
const MAX_TURNS_HARD_CAP = 12;
const MAX_OUTPUT_TOKENS = 30_000;
const READ_ONLY_TOOL_NAMES = new Set(["read", "grep", "find"]);

export class ScoutController {
	private readonly deps: ScoutControllerDeps;

	constructor(deps: ScoutControllerDeps) {
		this.deps = deps;
	}

	async run(query: string, maxTurns = MAX_TURNS_DEFAULT): Promise<ScoutRunResult> {
		const turnLimit = clampTurns(maxTurns);
		const unavailable = emptyResult();
		if (this.deps.signal?.aborted) {
			return { ...unavailable, failure: "aborted" };
		}

		let modelResolution: Awaited<ReturnType<ScoutControllerDeps["resolveScoutModel"]>>;
		try {
			modelResolution = await this.deps.resolveScoutModel();
		} catch (error) {
			return { ...unavailable, failure: error instanceof Error ? error.message : String(error) };
		}
		if ("failure" in modelResolution) {
			return { ...unavailable, failure: modelResolution.failure };
		}

		// Registered while this scout's own Agent drives turns, so the reload gate waits it out.
		// registerInFlightWork is a pure sync map op (cannot throw), so wrapping everything from here
		// through the end in try/finally guarantees deregistration on every exit path. Optional dep —
		// a caller that hasn't wired an agent dir through stays invisible to the gate, matching the
		// behavior before this registration was added.
		const deregisterInFlight = this.deps.getAgentDir
			? registerInFlightWork(this.deps.getAgentDir(), "scout", query.slice(0, 80))
			: undefined;
		try {
			let turnsUsed = 0;
			let truncated = false;
			let lastAssistantText = "";
			let outputTokens = 0;
			const agent = new Agent({
				initialState: {
					model: modelResolution.model,
					systemPrompt: SCOUT_SYSTEM_PROMPT.replace("{MAX_TURNS}", String(turnLimit)),
					tools: this.deps
						.buildReadOnlyTools(this.deps.getCwd())
						.filter((tool) => READ_ONLY_TOOL_NAMES.has(tool.name)),
				},
				streamFn: this.deps.streamFn,
				getApiKey: () => modelResolution.apiKey,
				textToolCallProtocol: modelResolution.textToolCallProtocol,
				maxStallTurns: turnLimit,
			});

			const unsubscribe = agent.subscribe((event) => {
				if (event.type !== "message_end" || event.message.role !== "assistant") {
					return;
				}
				turnsUsed += 1;
				lastAssistantText = assistantText(event.message);
				outputTokens += getScoutOutputTokenCount(event.message);
				this.deps.onEvent?.({ type: "scout_turn", detail: `turn ${turnsUsed}` });
				if (hasFinalAnswer(lastAssistantText)) {
					return;
				}
				if (turnsUsed >= turnLimit || outputTokens >= MAX_OUTPUT_TOKENS) {
					truncated = true;
					agent.abort();
				}
			});

			const abortScout = (): void => {
				truncated = true;
				agent.abort();
			};
			this.deps.signal?.addEventListener("abort", abortScout, { once: true });
			let runFailure: string | undefined;
			try {
				await agent.prompt(query);
			} catch (error) {
				if (!truncated && !this.deps.signal?.aborted) {
					runFailure = error instanceof Error ? error.message : String(error);
				}
			} finally {
				unsubscribe();
				this.deps.signal?.removeEventListener("abort", abortScout);
			}

			const result = parseScoutAnswer(
				lastAssistantText,
				(path) => this.deps.fileExists(path),
				(path) => this.deps.countLines(path),
			);
			const finalResult = {
				...result,
				truncated: truncated || !hasFinalAnswer(lastAssistantText),
				turnsUsed,
				failure: this.deps.signal?.aborted ? "aborted" : runFailure,
			};
			this.deps.onEvent?.({ type: "scout_end", detail: finalResult.failure ?? "ok" });
			return finalResult;
		} finally {
			deregisterInFlight?.();
		}
	}
}

export function getScoutOutputTokenCount(message: { usage?: { output?: number } }): number {
	return message.usage?.output ?? 0;
}

export function parseScoutAnswer(
	text: string,
	fileExists: (path: string) => boolean,
	countLines: (path: string) => number | undefined,
): Omit<ScoutRunResult, "truncated" | "turnsUsed" | "failure"> {
	const finalAnswer = /<final_answer>([\s\S]*?)<\/final_answer>/i.exec(text)?.[1] ?? text;
	const summaryLines: string[] = [];
	const citations: ScoutCitation[] = [];

	for (const rawLine of finalAnswer.split(/\r?\n/)) {
		const line = rawLine.trim();
		if (!line) continue;
		const citation = parseCitationLine(line, fileExists, countLines);
		if (citation) {
			citations.push(citation);
			continue;
		}
		summaryLines.push(line);
	}

	const droppedCitations = citations.filter((citation) => !citation.valid).length;
	return {
		summary: summaryLines.join("\n"),
		citations,
		droppedCitations,
		unreliable: citations.length > 0 && droppedCitations / citations.length > 0.5,
	};
}

function parseCitationLine(
	line: string,
	fileExists: (path: string) => boolean,
	countLines: (path: string) => number | undefined,
): ScoutCitation | undefined {
	const match = /^(.+?):(\d+)(?:-(\d+))?$/.exec(line);
	if (!match) return undefined;
	const path = match[1].trim();
	const start = Number.parseInt(match[2], 10);
	const end = Number.parseInt(match[3] ?? match[2], 10);
	const lineCount = countLines(path);
	const valid =
		fileExists(path) &&
		Number.isFinite(start) &&
		Number.isFinite(end) &&
		start >= 1 &&
		end >= start &&
		(lineCount === undefined || end <= lineCount);
	return { path, start, end, valid };
}

function clampTurns(maxTurns: number): number {
	if (!Number.isFinite(maxTurns)) return MAX_TURNS_DEFAULT;
	return Math.min(MAX_TURNS_HARD_CAP, Math.max(1, Math.trunc(maxTurns)));
}

function emptyResult(): ScoutRunResult {
	return { summary: "", citations: [], droppedCitations: 0, unreliable: false, truncated: false, turnsUsed: 0 };
}

function hasFinalAnswer(text: string): boolean {
	return /<final_answer>[\s\S]*?<\/final_answer>/i.test(text);
}

function assistantText(message: { content: unknown }): string {
	if (!Array.isArray(message.content)) return "";
	return message.content
		.filter((content): content is { type: "text"; text: string } =>
			Boolean(
				content &&
					typeof content === "object" &&
					(content as { type?: unknown }).type === "text" &&
					typeof (content as { text?: unknown }).text === "string",
			),
		)
		.map((content) => content.text)
		.join("\n");
}
